<template>
  <div class="border rounded bg-body-emphasis mt-3 overflow-hidden">
    <DataTable
      :value="rows"
      dataKey="id"
      :expandedRows
      tableClass="table table-sm mb-0"
      @update:expandedRows="expandedRows = expandedRowsByKey($event)"
    >
      <template #empty>No activity matches these filters.</template>
      <Column expander :headerStyle="{ width: '3rem' }" />
      <Column header="Time"
        ><template #body="{ data }"
          ><span class="text-nowrap">{{
            formatDateTime(data.created_at)
          }}</span></template
        ></Column
      >
      <Column header="Activity"
        ><template #body="{ data }"
          ><button
            v-if="data.activity_key"
            class="btn btn-link btn-sm p-0 text-start"
            title="Show only this activity"
            @click="$emit('filter', { activity_keys: [data.activity_key] })"
          >
            {{ data.activity_key }}
          </button></template
        ></Column
      >
      <Column header="Actor"
        ><template #body="{ data }"
          ><button
            class="btn btn-link btn-sm p-0 text-start truncate"
            :title="data.account.email"
            @click="$emit('filter', { account_id: data.account.id })"
          >
            {{ data.account.email }}</button
          ><span
            v-if="data.api_key_id"
            class="badge bg-info-subtle text-info-emphasis ms-1"
            >API key #{{ data.api_key_id }}</span
          ><span
            v-if="data.impersonated_account_id"
            class="badge bg-warning-subtle text-warning-emphasis ms-1"
            >As account #{{ data.impersonated_account_id }}</span
          ></template
        ></Column
      >
      <Column header="Company"
        ><template #body="{ data }"
          ><template v-if="data.company"
            ><button
              class="btn btn-link btn-sm p-0 text-start truncate"
              :title="data.company.name"
              @click="$emit('filter', { company_id: data.company.id })"
            >
              {{ data.company.name }}</button
            ><span
              v-if="data.company.deleted"
              class="badge bg-secondary-subtle text-secondary-emphasis ms-1"
              >Archived</span
            ></template
          ></template
        ></Column
      >
      <Column header="Linked"
        ><template #body="{ data }"
          ><span class="d-flex flex-wrap gap-2 small"
            ><template v-for="link in links(data)" :key="link.label"
              ><button
                v-if="link.patch"
                class="btn btn-link btn-sm p-0"
                :title="`Show only activity on ${link.label}`"
                @click="$emit('filter', link.patch)"
              >
                {{ link.label }}</button
              ><span v-else class="text-muted">{{ link.label }}</span></template
            ></span
          ></template
        ></Column
      >
      <Column header="ID"
        ><template #body="{ data }">#{{ data.id }}</template></Column
      >
      <template #expansion="{ data }">
        <pre class="metadata small mb-0">{{
          JSON.stringify(data.metadata, null, 2)
        }}</pre>
      </template>
    </DataTable>
    <p v-if="moreFailed" class="text-danger small p-3 mb-0" role="alert">
      Could not load older activity. Try again.
    </p>
    <button
      v-if="hasMore"
      class="btn btn-link w-100"
      :disabled="loadingMore"
      @click="$emit('more')"
    >
      {{ loadingMore ? "Loading…" : "Load more" }}
    </button>
  </div>
</template>
<script setup lang="ts">
import DataTable, { type DataTableExpandedRows } from "primevue/datatable";
import Column from "primevue/column";
import { ref } from "vue";
import { expandedRowsByKey } from "@/components/common/dataTableModels";
import { formatDateTime } from "@/composables/formatUtils";
import type { ActivityLogEntry, ActivityLogFilters } from "./activityLogTypes";

defineProps<{
  rows: ActivityLogEntry[];
  hasMore: boolean;
  loadingMore: boolean;
  moreFailed: boolean;
}>();
defineEmits<{ filter: [patch: ActivityLogFilters]; more: [] }>();

const expandedRows = ref<DataTableExpandedRows>({});

interface LinkedRecord {
  label: string;
  // Generators have no filter: the column is not indexed.
  patch?: ActivityLogFilters;
}

function links(entry: ActivityLogEntry): LinkedRecord[] {
  const records: (LinkedRecord | null)[] = [
    entry.order_id
      ? {
          label: `Order #${entry.order_id}`,
          patch: { order_id: entry.order_id },
        }
      : null,
    entry.portfolio_id
      ? {
          label: `Portfolio #${entry.portfolio_id}`,
          patch: { portfolio_id: entry.portfolio_id },
        }
      : null,
    entry.sales_transaction_id
      ? {
          label: `Sales transaction #${entry.sales_transaction_id}`,
          patch: { sales_transaction_id: entry.sales_transaction_id },
        }
      : null,
    entry.generator_id ? { label: `Generator #${entry.generator_id}` } : null,
  ];
  return records.filter((record) => record !== null);
}
</script>
<style scoped lang="scss">
.truncate {
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.metadata {
  max-height: 400px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
